import { useEffect, useState } from 'react';
import useDocumentTitle from '../../../../hooks/useDocumentTitle';
import { GetBusinessType, GetCustomerGroup } from '../../../../utils/HelperFunctions';
import { GlobalService } from '../../../../services/GlobalService';
import { UserService } from '../../../../services/UserService';
import { ToasterService } from '../../../../services/ToasterService';
import { useNavigate } from 'react-router-dom';

type FormData = {
  fullName: string;
  mobile: string;
  shopName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  country: string;
  pincode: string;
  openingTimeFrom: string;
  openingTimeTo: string;
  deliveryTimeFrom: string;
  deliveryTimeTo: string;
  businessType: string;
  gstRegistered: false;
  customerGroup: string;
  gst_number: string;
};
type Errors = Partial<Record<keyof FormData, string>>;
export default function UseSignUpPage() {
  // useDocumentTitle('Namasteji Manikchand - SignUp');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Errors>({});
  const [stateList, setStateList] = useState<string[]>([]);
  const [countryList, setCountryList] = useState<{ name: string }[]>([]);
  const [businessTypeOptions, setBusinessTypes] = useState<any>([]);
  const [customerGroupOptions, setCustomerGroup] = useState<any>([]);
  const [selectedBusinessType, setSelectedBusinessType] = useState<any>(null);
  const [selectedCustomerGroup, setSelectedCustomerGroup] = useState<any>(null);
  const [stepTitle, setStepTitle] = useState<string>('Personal Information');
  const newErrors: Errors = {};
  const [showGstNumberInput, setShowGstNumberInput] = useState<boolean>(false);

  const [formData, setFormData] = useState({
    fullName: '',
    mobile: '',
    shopName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    openingTimeFrom: '09:00',
    openingTimeTo: '20:00',
    deliveryTimeFrom: '10:00',
    deliveryTimeTo: '17:00',
    businessType: '',
    customerGroup: '',
    gstRegistered: false,
    gst_number: '',
  });

  const stepsMobileArr = [
    { id: 1, label: 'Personal Information' },
    { id: 2, label: 'Location Information' },
    { id: 3, label: 'Other Information' },
  ];

  const states = {
    step,
    setStep,
    errors,
    setErrors,
    formData,
    setFormData,
    stateList,
    setStateList,
    countryList,
    setCountryList,
    stepsMobileArr,
    selectedBusinessType,
    selectedCustomerGroup,
    businessTypeOptions,
    customerGroupOptions,
    stepTitle,
    showGstNumberInput,
  };

  useEffect(() => {
    (async () => {
      const getBusinessTypes = await GetBusinessType();
      setBusinessTypes(getBusinessTypes);
      const getCustomerGroup = await GetCustomerGroup();
      setCustomerGroup(getCustomerGroup);
    })();
    getState();
    getCountry();
  }, []);

  const getState = async () => {
    const resp = await GlobalService.State();
    setStateList(resp.message);
  };

  const getCountry = async () => {
    const resp = await GlobalService.Country();
    setCountryList(resp.message);
  };

  const validateStep1 = (): boolean => {
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full Name is required';
    }
    const mobile = formData.mobile.trim();
    if (!mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d+$/.test(mobile)) {
      newErrors.mobile = 'Mobile number must contain only digits';
    } else if (mobile.length !== 10) {
      newErrors.mobile = 'Mobile number must be exactly 10 digits';
    } else if (!/^[6-9]\d{9}$/.test(mobile)) {
      newErrors.mobile = 'Enter a valid 10-digit Indian mobile number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    if (!formData.shopName.trim()) {
      newErrors.shopName = 'Shop Name is required';
    }
    if (!formData.address1.trim()) {
      newErrors.address1 = 'Address-1 is required';
    }
    if (!formData.address2?.trim()) {
      newErrors.address2 = 'Address-2 is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'Please Select your state';
    }
    if (!formData.country.trim()) {
      newErrors.country = 'Please select country';
    }
    const pincode = formData.pincode.trim();
    if (!pincode) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d+$/.test(pincode)) {
      newErrors.pincode = 'Pincode must contain only digits';
    } else if (pincode.length !== 6) {
      newErrors.pincode = 'Pincode must be exactly 6 digits';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = (): boolean => {
    if (!formData.openingTimeFrom.trim()) {
      newErrors.openingTimeFrom = 'Opening Time is required';
    }
    if (!formData.openingTimeTo.trim()) {
      newErrors.openingTimeTo = 'Closeing time is required.';
    }
    if (!formData.deliveryTimeFrom.trim()) {
      newErrors.deliveryTimeFrom = 'Delivery From Time is required.';
    }
    if (!formData.deliveryTimeTo.trim()) {
      newErrors.deliveryTimeTo = 'Delivery To time is required.';
    }
    if (!formData.businessType.trim()) {
      newErrors.businessType = 'Business type is required';
    }
    if (!formData.customerGroup.trim()) {
      newErrors.customerGroup = 'Customer Group is required';
    }
    if (showGstNumberInput) {
      const gst = formData.gst_number.trim();
      if (!gst) {
        newErrors.gst_number = 'GST Number is required';
      } else {
        const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
        if (!gstRegex.test(gst)) {
          newErrors.gst_number = 'Invalid GST Number format';
        }
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    if (name === 'gstRegistered') {
      if (checked) {
        setShowGstNumberInput(true);
      } else {
        setShowGstNumberInput(false);
        formData.gst_number = '';
      }
    }
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleChangeBusineType = (selectedOption: any) => {
    setSelectedBusinessType(selectedOption);
    setFormData(prev => ({
      ...prev,
      businessType: selectedOption ? selectedOption.value : '',
    }));

    setErrors(prev => ({
      ...prev,
      businessType: selectedOption ? '' : prev.customerGroup,
    }));
  };

  const handleChangeCustomerGroup = (selectedOption: any) => {
    setSelectedCustomerGroup(selectedOption);
    setFormData(prev => ({
      ...prev,
      customerGroup: selectedOption ? selectedOption.value : '',
    }));

    setErrors(prev => ({
      ...prev,
      customerGroup: selectedOption ? '' : prev.customerGroup,
    }));
  };

  const handleNext = () => {
    if (step === 1) {
      setStepTitle('Personal Information');
      if (validateStep1()) {
        setStep(step + 1);
        setStepTitle('Location Information');
      }
    } else if (step === 2) {
      if (validateStep2()) {
        setStepTitle('Other Information');
        setStep(step + 1);
      }
    }
    // else if (step == 3) {
    //   if (validateStep3()) {
    //     setStep(step + 1);
    //   }
    // }
  };

  // const handleBack = () => {
  //     if (step > 1) setStep(step - 1);
  // };
  const handleBack = () => {
    if (step > 1) {
      const newStep = step - 1;
      setStep(newStep);
      if (newStep === 1) {
        setStepTitle('Personal Information');
      } else if (newStep === 2) {
        setStepTitle('Location Information');
      }
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    if (validateStep3()) {
      try {
        const payload = {
          customer_name: formData.fullName,
          mobile_no: formData.mobile,
          address_line1: formData.address1,
          address_line2: formData.address2,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          country: formData.country,
          customer_group: formData.customerGroup,
          start_time: formData.openingTimeFrom,
          end_time: formData.openingTimeTo,
          from_time: formData.deliveryTimeFrom,
          to_time: formData.deliveryTimeTo,
          industry: formData.businessType,
          shop_name: formData.shopName,
          // gst: formData.gst_number,
        };
        const resp = await UserService.SignUp(payload);
        localStorage.clear();
        if (resp && resp.message && resp.message.status_code === 200) {
          ToasterService.Success('Signup successful!');
          navigate('/login');
        } else if (resp && resp.message && resp.message.status_code === 400) {
          ToasterService.Error('Mobile Number Already Registered!');
          navigate('/login');
        } else {
          ToasterService.Error('Something went wrong. Please try again later.');
          navigate('/home');
        }
      } catch (err) {
        ToasterService.Error('Server Error!.. Try Again after sometime');
        navigate('/home');
      }
    }
  };

  return {
    handleBack,
    handleSubmit,
    handleNext,
    handleChange,
    states,
    handleChangeBusineType,
    handleChangeCustomerGroup,
  };
}
